import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { OrderService } from '../../../services/order.service';
import { ActivatedRoute, Router } from '@angular/router';
import { isNullOrUndefined } from 'is-what';
import { flatMap } from 'lodash';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MessageService } from 'primeng/api';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Paginator } from 'primeng/paginator';
import { StorageService } from 'src/app/services/storage.service';
import { NgxSpinnerService } from 'ngx-spinner';
import saveAs from 'file-saver';
import { Subject, takeUntil } from 'rxjs';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { TableColumn, ColumnVisibility, DataTableComponent } from 'src/app/shared-components/data-table/data-table.component';

export function noSpacesValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (control.value && typeof control.value === 'string') {
      if (control.value.trim().length === 0) {
        return { noSpaces: true };
      }
    }
    return null;
  };
}

@Component({
    selector: 'app-individual-order',
    templateUrl: './individual-order.component.html',
    styleUrls: ['./individual-order.component.scss'],
    standalone: false
})
export class IndividualOrderComponent implements OnInit, OnDestroy, OnChanges {
  @Input() orderOption: any;
  @Output() individualOrderChange: EventEmitter<any> = new EventEmitter<[]>();
  @ViewChild('paginatorComponent') paginator: Paginator;
  @Output() viewOrderRequested = new EventEmitter<any>();
  @ViewChild('viewOrderData') viewOrderData: TemplateRef<any>;
  @ViewChild('dataTable') dataTable: DataTableComponent;

  draftOrders: { [key: number]: boolean | null } = {};

  readonly columns: TableColumn[] = [
    { field: 'id', header: 'Order No', sortable: true, visibilityKey: 'order_no' },
    { field: 'user', header: 'Ordered by', sortable: true, visibilityKey: 'ordered_by' },
    { field: 'referance_id', header: 'Ref. ID', sortable: true, visibilityKey: 'ref_id' },
    {
      field: 'estamp_info.0.second_party_name',
      sortField: 'second_party',
      header: 'Second Party',
      sortable: true,
      visibilityKey: 'second_party',
      fallback: '----',
      truncate: 20,
      tooltip: true,
    },
    {
      field: 'created',
      header: 'Order Date',
      sortable: true,
      visibilityKey: 'order_date',
      pipe: 'date:dd/MM/yyyy hh:mm a',
    },
    { field: 'order_type', header: 'Order Type', sortable: true, visibilityKey: 'order_type' },
    { field: 'status', header: 'Status', sortable: true, visibilityKey: 'status' },
    { field: 'branch', header: 'Branch', sortable: true, visibilityKey: 'branch' },
    {
      field: 'amount',
      header: 'Stamp Amount',
      sortable: true,
      visibilityKey: 'stamp_amount',
      accessorFn: (row) => (row.estamp_info?.length > 0 ? this.calculateTotalStampDuty(row.estamp_info) : null),
      fallback: 'NA',
    },
    {
      field: 'estamp_info.0.first_party_name',
      header: 'First Party Name',
      visibilityKey: 'first_party_name',
      fallback: '----',
    },
    {
      field: 'estamp_info.0.stamp_duty_paid_by',
      header: 'Stamp Duty Paid by',
      visibilityKey: 'stamp_duty_paid_by',
      fallback: '----',
    },
    { field: 'action', header: 'Action', toggleable: false, draggable: false },
  ];

  readonly rowClassFor = (row: any): string =>
    this.id !== null && row.id == this.id ? 'selectedRow' : '';

  pageNo: number = 1;
  totalRecords: number = 10;
  isDateRangeValid: boolean = false;
  start_date_filter: Date;
  end_date_filter: Date;
  orders: any = [];
  selectedOrder: any;
  orderFilters = {
    action_type: 'individual',
    page_number: 1,
    page_size: 10,
    account_id: '',
  };
  id: any;
  enableCancel: boolean = true;
  today = new Date();
  formattedDateTime: string;
  currentSortColumn: string = '';
  sortDirection: string = 'asc';
  cancelOrderId: string;
  CancelledReason: string = '';
  submitted: boolean = false;
  pdfSource: any;
  fileName: string = 'Choose File';
  selectedFile: any;
  editOrderId: any;
  editEstampForm: FormGroup;
  stampDutyPaidByOptions = [
    { name: 'First Party', code: 'first_party_name' },
    { name: 'Second Party', code: 'second_party_name' },
  ];

  stateData: any;
  branchData: any;
  minDate = new Date();
  estampForms: FormArray;
  commonFieldsOrderForm: FormGroup;
  esignForms: FormArray;
  orderDetail: any;
  public phonePattern: string = '[0-9]{10}';
  editEsignForm: FormGroup;
  rowsPerPageOptions: number[];
  rowsPerPage: any = 10;
  searchPageNumber: number = 1;
  numberoftables: number;
  userRole: string;
  viewOrderId: any;
  private destroy$ = new Subject<void>();
  constructor(
    private cookieService: CookieService,
    private orderService: OrderService,
    private router: Router,
    private route: ActivatedRoute,
    private modalService: NgbModal,
    private messageService: MessageService,
    private formBuilder: FormBuilder,
    private storageService: StorageService,
    private spinner: NgxSpinnerService,
    private cdr: ChangeDetectorRef,
    private analyticsService: AnalyticsService
  ) {
    const year = this.today.getFullYear();
    const month = String(this.today.getMonth() + 1).padStart(2, '0');
    const day = String(this.today.getDate()).padStart(2, '0');
    const hours = this.today.getHours();
    const minutes = String(this.today.getMinutes()).padStart(2, '0');
    const meridiem = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = (hours % 12 || 12).toString().padStart(2, '0');
    this.formattedDateTime = `${year}-${month}-${day} ${formattedHours}:${minutes} ${meridiem}`;

    this.commonFieldsOrderForm = this.formBuilder.group({
      referenceNumber: [''],
      branch: [''],
      state: [''],
    });

    this.estampForms = this.formBuilder.array([]);

    this.editEstampForm = this.formBuilder.group({
      estamp_id: [''],
      secondPartyName: [''],
      stampDutyValue: [''],
      stampDutyPaidBy: [''],
      execution_date: [''],
      estamp_info: this.estampForms,
    });

    this.esignForms = this.formBuilder.array([]);

    this.editEsignForm = this.formBuilder.group({
      esign_id: [''],
      name: [''],
      email: [''],
      contact_number: [''],
      esign_info: this.esignForms,
    });

    this.userRole = this.storageService.getRole() || null;
    this.orderFilters['account_id'] =
      this.storageService.getAccountId() || null;
  }

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id');
    this.route.queryParams.subscribe((queryParams) => {
      this.id = queryParams?.id || queryParams?.openOrderId;
    });
    this.getOrders(this.orderFilters)
    this.getStateDetails();
    this.getUserSettingsData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['orderOption']) {
      if (changes['orderOption'].currentValue['tabIndex'] == 0) {
        const branch_ids = changes['orderOption'].currentValue['branch_ids'];
        const searchText = changes['orderOption'].currentValue['searchText'];
        const serviceType = changes['orderOption'].currentValue['serviceType'];
        const orderStatus = changes['orderOption'].currentValue['orderStatus'];
        const selectedStatuses = Array.isArray(orderStatus)
          ? orderStatus
          : orderStatus
            ? [orderStatus]
            : [];
        const startDate = changes['orderOption'].currentValue['startDate'];
        this.start_date_filter = startDate;
        const endDate = changes['orderOption'].currentValue['endDate'];
        this.end_date_filter = endDate;
        const orderIds = changes['orderOption'].currentValue['order_ids'];

        const searchOrderId =
          changes['orderOption'].currentValue['searchOrderId'];

        if (startDate && endDate) {
          const start = new Date(startDate);
          const end = new Date(endDate);

          const timeDiff = Math.abs(end.getTime() - start.getTime());
          const dayDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

          this.isDateRangeValid = dayDiff <= 7;
        } else {
          this.isDateRangeValid = false;
        }

        if (orderIds) {
          this.orderFilters['order_ids'] = orderIds;
        } else {
          delete this.orderFilters['order_ids'];
        }

        if (searchOrderId) {
          this.orderFilters['order_id'] = searchOrderId;
          // Reset pagination when searching by order ID
          delete this.orderFilters['page_number'];
          delete this.orderFilters['page_size'];
          delete this.orderFilters['action_type'];
        } else {
          delete this.orderFilters['order_id'];
        }

        if (searchText.length > 1) {
          this.orderFilters['search'] = searchText;
          delete this.orderFilters['action_type'];
          delete this.orderFilters['page_number'];
          delete this.orderFilters['page_size'];
        } else {
          delete this.orderFilters['search'];
        }
        // if (branch_ids && !isNullOrUndefined(branch_ids)) {
        //   this.orderFilters["branch_ids"] = branch_ids;
        // } else {
        //   delete this.orderFilters["branch_ids"];
        // }

        if (branch_ids && branch_ids.length != 0) {
          this.orderFilters['branch_ids'] = branch_ids;
        } else {
          delete this.orderFilters['branch_ids'];
        }

        if (serviceType && !isNullOrUndefined(serviceType)) {
          this.orderFilters['order_type'] = serviceType;
        } else {
          delete this.orderFilters['order_type'];
        }

        if (selectedStatuses.length > 0) {
          this.orderFilters['status'] = selectedStatuses.join(',');
        } else {
          delete this.orderFilters['status'];
        }
        delete this.orderFilters['payment_status'];

        if (
          startDate &&
          endDate &&
          !isNullOrUndefined(startDate) &&
          !isNullOrUndefined(endDate)
        ) {
          this.orderFilters['start_date'] = startDate;
          this.orderFilters['end_date'] = endDate;
        } else {
          delete this.orderFilters['start_date'];
          delete this.orderFilters['end_date'];
        }
        // A newly selected status must start from the first page so matching
        // orders are not hidden by the previous page number.
        this.orderFilters['page_number'] = 1;
        this.getOrders(this.orderFilters);
      }
    }
  }

  viewOrderDetails(orderId: any) {
    this.viewOrderRequested.emit(orderId);
  }

  getDisplayStatus(order: any): string {
    return order?.status;
  }

  private normalizeOrderStatus(status: any): string {
    return String(status ?? '')
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, '_');
  }

  getSelectedOrderPdf() {
    const id = this.orderFilters['account_id'];
    const start_date = this.start_date_filter;
    const end_date = this.end_date_filter;
    const branch_ids = this.orderFilters['branch_ids'];
    const order_type = this.orderFilters['order_type'];
    const order_status = this.orderFilters['status'];
    const search_by_ref_id = this.orderFilters['search'];

    const data = {
      account_id: id ?? '',
      start_date: start_date ?? '',
      end_date: end_date ?? '',
      branch_ids: branch_ids ?? '',
      order_type: order_type ?? '',
      order_status: order_status ?? '',
      search: search_by_ref_id ?? '',
    };

    console.log(data);
    this.orderService.getAllBulkOrderPdfData(data).subscribe(
      (res: Blob) => {
        const blob = new Blob([res], { type: 'application/zip' });

        saveAs(blob, `Orders from ${start_date} to ${end_date}`);
      },
      (error: any) => {
        console.log(error);
        this.messageService.add({
          severity: 'error',
          summary: 'File Not Found',
        });
      }
    );
  }

  sendtoOrderListing() {
    this.individualOrderChange.emit('Individual');
  }

  ngOnDestroy() {
    this.cookieService.set('isBulkData', 'false');
    this.destroy$.next();
    this.destroy$.complete();
  }

  getOrders(params) {
    this.orderService.getOrders(params).subscribe(
      (resp) => {
        if (resp.status === 200 && resp.message !== 'No records found') {
          this.orders = [...resp.content];
          const selectedStatuses = Array.isArray(this.orderOption?.orderStatus)
            ? this.orderOption.orderStatus
            : this.orderOption?.orderStatus
              ? [this.orderOption.orderStatus]
              : [];

          if (selectedStatuses.length > 0) {
            const selectedStatusCodes = selectedStatuses.map((status) =>
              this.normalizeOrderStatus(status),
            );
            this.orders = this.orders.filter((order) => {
              const effectiveStatus = this.normalizeOrderStatus(
                order?.payment_data?.status === 'PAYMENT_SUCCESSFUL'
                  ? order.payment_data.status
                  : order?.status,
              );
              return selectedStatusCodes.includes(effectiveStatus);
            });
          }
          this.totalRecords = resp.pagination.total_records;
          this.searchPageNumber = resp.pagination.page_number;
          this.numberoftables = Math.ceil(this.totalRecords / this.rowsPerPage);

          if (this.orderOption.searchOrderId) {
            this.orders = this.orders.filter((order) =>
              order.id
                .toString()
                .includes(this.orderOption.searchOrderId.toString())
            );
            this.totalRecords = this.orders.length;
          }
          const uniqueReferanceIds = {};
          this.orders = this.orders.filter((item) => {
            if (!uniqueReferanceIds[item.referance_id]) {
              uniqueReferanceIds[item.referance_id] = true;
              return true;
            }
            return false;
          });
          this.orders.forEach((element) => {
            if (element.created && typeof element.created === 'string') {
              const parts = element.created.trim().split(/\s+/);
              if (parts.length === 3) {
                const [datePart, timePart, meridiem] = parts;
                const [hourStr, minuteStr] = timePart.split(':');
                let hours = parseInt(hourStr, 10);
                if (meridiem.toUpperCase() === 'PM' && hours !== 12) hours += 12;
                if (meridiem.toUpperCase() === 'AM' && hours === 12) hours = 0;
                element.created = `${datePart}T${hours.toString().padStart(2, '0')}:${minuteStr}:00`;
              }
            }
            if (element.status === 'Waiting for Approval') {
              element.status = 'Approval Pending';
            }
          });

          if (this.id) {
          const autoOrder = this.orders.find(order => order.id.toString() === this.id.toString());

          if (autoOrder) {
            this.viewOrder(autoOrder, this.viewOrderData);
            this.id = null;
          }
        }

        } else {
          this.orders = [];
          this.totalRecords = 10;
        }
        this.cdr.detectChanges();
      },
      (err) => {
        this.orders = [];
        this.totalRecords = 10;
        if (
          'error' in err &&
          'content' in err.error &&
          'errors' in err.error.content
        ) {
          for (let i = 0; i < err.error.content.errors.length; i++) {
            this.messageService.add({
              severity: 'error',
              detail: err.error.content.errors[i],
            });
          }
        }
        this.cdr.detectChanges();
      }
    );
  }

  editDraftOrder(order: any) {
      this.storageService.setDraftMode(true);
      this.orderService.getDraftOrder(order.id).subscribe({
       next: (res) => {
      if (res && res.status === 200) {
        this.storageService.setDraftData(res.content); 
          this.router.navigate(['/doqfy/cat-execution/form'], {
           queryParams: { 
             service: 'multiple-stamp',
             order_id: order.id 
           }
         });
      }
       },
       error: (err) => {
         console.error('Draft load failed:', err);
         this.router.navigate(['/doqfy/cat-execution/form'], {
           queryParams: { service: 'multiple-stamp', order_id: order.id }
          });
       }
     });
    }

  back() {
    this.userRole === 'CLIENT'
      ? this.router.navigate(['doqfy/dashboard'])
      : this.router.navigate(['doqfy/admin-dashboard']);
  }

  onPageChange(event: any) {
    if (event) {
      this.pageNo = event.first;
      this.orderFilters['page_number'] = event.page + 1;
      this.orderFilters['page_size'] = event.rows;
      this.getOrders(this.orderFilters);
    }
    this.rowsPerPage = event.rows;
  }

  pageChange(page: number) {
    if (this.paginator) {
      this.paginator.changePage(page - 1);
    }
  }

  sort(column: string): void {
    if (column === this.currentSortColumn) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.currentSortColumn = column;
      this.sortDirection = 'asc';
    }

    this.orders.sort((a, b) => {
      const valueA = a[column];
      const valueB = b[column];
      if (valueA < valueB) {
        return this.sortDirection === 'asc' ? -1 : 1;
      } else if (valueA > valueB) {
        return this.sortDirection === 'asc' ? 1 : -1;
      } else {
        return 0;
      }
    });
  }

  cancelOrder(order, content) {
   this.analyticsService.trackEvent('click_cancel_order_individual', {
    order_id: order.id,
    org_id: order.account?.id || null,
    status: order.status
   });
  
    this.cancelOrderId = order.id;
    this.modalService
      .open(content, {
        size: 'lg',
        ariaLabelledBy: 'modal-basic-title',
        backdrop: 'static',
        keyboard: false,
      })
      .result.then(
        (result) => {
          console.log(result);
        },
        (reason) => {
          console.log(reason);
        }
      );
  }

  viewOrder(order, content) {
    this.viewOrderId = { orderId: order.id, bulkOrderid: null };

    this.analyticsService.trackEvent('click_view_order_individual', {
      order_id: order.id,
      org_id: order.account?.id || null,
      organisation_name: order.account?.organisation_name || null,
      account_name: order.account?.account_name || null,
      order_status: order.status || null
    });

    this.modalService
      .open(content, {
        size: 'xl',
        ariaLabelledBy: 'modal-basic-title',
        backdrop: 'static',
        keyboard: false,
      })
      .result.then(
        (result) => {
          console.log(result);
        },
        (reason) => {
          console.log(reason);
          this.getOrders(this.orderFilters);
        }
      );
  }

  cancelSelectedOrder() {
    this.submitted = true;
    if (this.CancelledReason === '') return;
    
      let data = {
        order_ids: [this.cancelOrderId],
        reason: this.CancelledReason,
        document: this.pdfSource,
        fileName: this.fileName,
      };

    this.orderService.cancelOrder(data).subscribe(
      (res) => {
        const failed: string[] = res?.content?.failed ?? [];

        if (failed.length > 0) {
          this.analyticsService.trackEvent('cancel_order_failure', {
            order_id: this.cancelOrderId,
            error_message: failed.join(', ')
          });
          failed.forEach((errorMsg: string) => {
            this.messageService.add({
              severity: 'error',
              detail: errorMsg,
            });
          });
        } else {
          this.analyticsService.trackEvent('cancel_order_success', {
            order_id: this.cancelOrderId,
            reason: this.CancelledReason
          });
          this.messageService.add({
            severity: 'success',
            detail: 'Order Cancelled Successfully',
          });
        }

        this.getOrders(this.orderFilters);
        this.closeModel();
        this.submitted = false;
      },
      (err) => {
        this.analyticsService.trackEvent('cancel_order_failure', {
          order_id: this.cancelOrderId,
          error_message: err?.error?.message || 'Unknown error'
        });
        this.messageService.add({
          severity: 'error',
          detail: err?.error?.message || 'Failed to cancel order. Please try again.',
        });
        this.submitted = false;
      }
    );
  }

  onFileChange(event: any) {
    let files = event.target.files;
    this.selectedFile = files[0];
    this.fileName = files[0].name;

    const fileSize = files[0].size / 1024 / 1024;
    if (fileSize > 10) {
      alert('File should be less than 10MB in size');
      const inputElement = <HTMLInputElement>(
        document.getElementById('upload_file')
      );
      inputElement.value = '';
      return;
    }

    let splitName = files[0].name.split('.');
    if (splitName.length > 2) {
      alert('File name should have only single extension');
      const inputElement = <HTMLInputElement>(
        document.getElementById('upload_file')
      );
      inputElement.value = '';
      return;
    }

    let $img: any = document.querySelector('#upload_file');

    if (typeof FileReader !== 'undefined') {
      let reader = new FileReader();

      reader.onload = (e: any) => {
        // Get the base64-encoded string
        this.pdfSource = e.target.result;
      };

      // Read the file as Data URL (base64)
      reader.readAsDataURL(files[0]);
    }
  }

  clearFile() {
    this.selectedFile = '';
    this.fileName = 'Choose File';
    const inputElement = <HTMLInputElement>(
      document.getElementById('upload_file')
    );
    inputElement.value = null;
  }

  getOrderDetail(orderId, content) {
    this.orderService.getOrderDetail(orderId).subscribe((res: any) => {
      let orderDetails = res['content'][0];
      this.openEditOrderPopup(orderDetails, content);
    });
  }

  openEditOrderPopup(orderDetails, content) {
    this.selectedOrder = orderDetails;

    this.getBranchDetails(orderDetails.state.id);
    this.editOrderId = orderDetails.order.id;

    this.commonFieldsOrderForm = this.formBuilder.group({
      referenceNumber: [orderDetails.order.referance_id || ''],
      branch: [orderDetails.order.branch || ''],
      state: [orderDetails.state.id || ''],
    });

    // Clear any existing forms from the form array
    this.estampForms.clear();

    // Loop through estamp_info and create a form for each item
    orderDetails.estamp.forEach((estampItem) => {
      let stampDutyPaidBy;
      if (
        estampItem.stamp_duty_paid_by === estampItem.first_party_name ||
        estampItem.stamp_duty_paid_by === 'first_party_name'
      )
        stampDutyPaidBy = 'first_party_name';
      else stampDutyPaidBy = 'second_party_name';
      let executionDate = new Date(estampItem.execution_date);
      this.editEstampForm = this.formBuilder.group({
        estamp_id: [estampItem.id],
        secondPartyName: [
          estampItem.second_party_name || '',
          [noSpacesValidator(), Validators.maxLength(50)],
        ],
        stampDutyValue: [
          estampItem.stamp_duty_amount || '',
          [Validators.min(10)],
        ],
        stampDutyPaidBy: [stampDutyPaidBy || ''],
        execution_date: [executionDate || ''],
      });

      // Push the form group into the estampForms array
      this.estampForms.push(this.editEstampForm);
    });

    //Esign edit form
    this.esignForms.clear();

    orderDetails.esign.forEach((esignItem) => {
      this.editEsignForm = this.formBuilder.group({
        esign_id: [esignItem.id],
        name: [
          esignItem.name || '',
          [noSpacesValidator(), Validators.maxLength(50)],
        ],
        email: [
          esignItem.email || '',
          [
            Validators.email,
            Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$'),
          ],
        ],
        contact_number: [
          esignItem.contact_number || '',
          [
            Validators.pattern(this.phonePattern),
            Validators.maxLength(10),
            Validators.minLength(10),
          ],
        ],
      });

      // Push the form group into the estampForms array
      this.esignForms.push(this.editEsignForm);
    });

    // Open the modal
    this.modalService
      .open(content, {
        size: 'lg',
        ariaLabelledBy: 'modal-basic-title',
        backdrop: 'static',
        keyboard: false,
      })
      .result.then(
        (result) => {
          console.log(result);
        },
        (reason) => {
          console.log(reason);
        }
      );
  }

  onEditStateChange(evt) {
    this.getBranchDetails(evt.value);
  }

  editSelectedOrder() {
    this.submitted = true;
    if (this.editEstampForm.valid) {
      let estampsData = [];
      let esignsData = [];

      this.estampForms.value.forEach((element) => {
        let obj = {
          estamp_id: element.estamp_id,
          second_party_name: element.secondPartyName,
          stamp_duty_amount: element.stampDutyValue,
          stamp_duty_paid_by: element.stampDutyPaidBy,
          execution_date: element.execution_date.getTime(),
        };
        estampsData.push(obj);
      });

      this.esignForms.value.forEach((element) => {
        let obj = {
          esign_id: element.esign_id,
          name: element.name,
          email: element.email,
          contact_number: element.contact_number,
        };
        esignsData.push(obj);
      });

      let payload = {
        order_id: this.editOrderId,
        branch_id: this.commonFieldsOrderForm.controls['branch'].value,
        reference_number:
          this.commonFieldsOrderForm.controls['referenceNumber'].value,

        estamps: estampsData,
        esigns: esignsData,
      };

      this.orderService.updateOrder(payload).subscribe((res) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Order Updated',
          detail: 'Order Updated Successfully',
        });
        this.getOrders(this.orderFilters);
        this.closeEditOrderPopup();
        this.submitted = false;
      });
    }
  }

  numberOnly(event: any) {
    const pattern = /[0-9\-]/;
    let inputChar = String.fromCharCode(event.charCode);
    if (
      (event.keyCode != 8 && !pattern.test(inputChar)) ||
      event.keyCode == 45
    ) {
      event.preventDefault();
    } else if (event.target.value === '0') {
      event.preventDefault();
    }
  }

  getStateDetails() {
    this.orderService.getStates().subscribe((res: any) => {
      this.stateData = res['content'];
    });
  }

  getBranchDetails(id: number) {
    this.orderService.getBranchListbyStateId(id).subscribe((res: any) => {
      this.branchData = res['content'];
      this.branchData = this.branchData.filter(
        (element) => element.is_active === true
      );
    });
  }

  dateEpochFormat(executionDate) {
    const date = new Date((executionDate - 1) * 24 * 60 * 60 * 1000); // Adjust for Excel's epoch (Jan 1, 1900)
    this.addZeroTime(date);
    this.changeYearToCurrent(date);
    return date.getTime();
  }

  addZeroTime(date) {
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
  }

  changeYearToCurrent(date) {
    const currentYear = new Date().getFullYear();
    date.setFullYear(currentYear);
  }

  closeEditOrderPopup() {
    this.modalService.dismissAll();
  }

  closeModel() {
    this.CancelledReason = '';
    this.clearFile();
    this.modalService.dismissAll();
  }

  calculateRowsPerPageOptions() {
    const options = [10, 20, 30, 50, 100]; // Default options

    // Calculate additional options based on totalRecords
    if (this.totalRecords > 100) {
      options.push(this.totalRecords); // Add the totalRecords as an option
    }

    // Sort the options array if needed
    this.rowsPerPageOptions = options.sort((a, b) => a - b);
  }

  onRowsPerPageChange(event: any) {
    this.rowsPerPage = event.value;
  }

  reOpenOrder(id) {
    const data = {
      order_ids: [id],
    };
    this.orderService.moveToPriority(data).subscribe((resp) => {
      if (resp.status == 200) {
        this.messageService.add({ severity: 'success', detail: resp.message });
      }
    });
  }

  calculateTotalStampDuty(estampInfo: any[]): number {
    return estampInfo.reduce((sum, item) => {
      const amount = parseFloat(item.stamp_duty_amount);
      return !isNaN(amount) ? sum + amount : sum;
    }, 0);
  }


  getUserSettingsData(): void {
    this.orderService.getUserSettings().subscribe({
      next: (res: any) => {
        const columnOptions: { key: string; is_visible: boolean }[] =
          res?.content?.table_configuration?.all_visualizations ?? [];
        for (const option of columnOptions) {
          const col = this.columns.find((c) => (c.visibilityKey || c.field) === option.key);
          if (col) {
            col.visible = option.is_visible;
          }
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error fetching user settings:', err);
      },
    });
  }

  onColumnVisibilityChange(changes: ColumnVisibility[]): void {
    const tableConfiguration: { [key: string]: boolean } = {};
    changes.forEach((c) => (tableConfiguration[c.field] = c.visible));

    this.orderService.saveUserSettings({ table_configuration: tableConfiguration }).subscribe({
      next: (res: any) => {
        console.log('Settings Saved', res);
      },
      error: (err) => {
        console.error('Save Failed', err);
      },
    });
  }

}